import React, { lazy, Suspense } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Target, BarChart3, Users, Zap } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { StructuredData, OrganizationSchema } from '@/components/StructuredData';
import IndustryShowcase from '@/components/IndustryShowcase';
import HeroSection from '@/components/HeroSection';
import ScrollReveal from '@/components/ScrollReveal';

// Lazy load ContactForm as it's below the fold
const ContactForm = lazy(() => import('@/components/ContactForm'));

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribeClick = () => {
    if (user) {
      navigate('/assessments');
    } else {
      navigate('/auth?returnUrl=/assessments');
    }
  };


  const benefits = [
    {
      icon: Target,
      title: 'Clarity',
      description: 'Understand your true position in sales and marketing.'
    },
    {
      icon: BarChart3,
      title: 'Focus',
      description: 'Eliminate wasted efforts and redirect resources to impact areas.'
    },
    {
      icon: Users,
      title: 'Alignment',
      description: 'Bring sales and marketing into a unified growth engine.'
    },
    {
      icon: Zap,
      title: 'Action',
      description: 'Move beyond insights with a clear roadmap for results.'
    }
  ];

  // Industries data moved to IndustryShowcase component

  const organizationSchema: OrganizationSchema = {
    '@type': 'Organization',
    name: 'GrowthLens',
    url: 'https://growthlens.in',
    logo: 'https://growthlens.in/src/assets/logo-default.png',
    description: 'B2B marketing consultancy that partners with SMEs to build structured, scalable growth models. Developed by SciMarq.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN'
    },
    sameAs: []
  };

  return (
    <>
      <SEOHead 
        title="GrowthLens - B2B Marketing Consultancy for SMEs"
        description="GrowthLens is a B2B marketing consultancy that partners with SMEs to build structured, scalable growth models. Get expert growth assessments and actionable insights."
        canonical="https://growthlens.in/"
      />
      <StructuredData data={organizationSchema} />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <HeroSection />

        {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--section-light))]" role="region" aria-labelledby="benefits-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12 sm:mb-16">
              <h2 id="benefits-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">Why Growth Lens Matters</h2>
              <div className="w-16 sm:w-20 h-1 bg-primary mx-auto" role="presentation" />
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <Card 
                  className="card-enhanced border-0 shadow-lg hover:shadow-xl focus-within:shadow-xl focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 transition-all duration-300 group h-full" 
                  tabIndex={0} 
                  role="article" 
                  aria-labelledby={`benefit-${index}-title`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                    }
                  }}
                >
                  <CardContent className="p-4 sm:p-6 text-center space-y-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto transition-colors duration-200 group-hover:bg-primary/20 group-focus-within:bg-primary/20" role="presentation">
                      <benefit.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary transition-transform duration-200 group-hover:scale-110 group-focus-within:scale-110" aria-hidden="true" />
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary transition-colors duration-200" aria-hidden="true" />
                      <h3 id={`benefit-${index}-title`} className="text-lg sm:text-xl font-semibold text-foreground">{benefit.title}</h3>
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Focus Section */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--section-dark))]" role="region" aria-labelledby="industry-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-10 sm:mb-12">
              <h2 id="industry-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">Ideal For B2B Manufacturers</h2>               
            </div>
          </ScrollReveal>
          <ScrollReveal animation="scale-up" delay={150}>
            <IndustryShowcase />
          </ScrollReveal>
        </div>
      </section>

      {/* What You Receive Section */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--section-light))]" role="region" aria-labelledby="receive-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12 sm:mb-16">
              <h2 id="receive-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">What You Receive</h2>
              <div className="w-16 sm:w-20 h-1 bg-primary mx-auto" role="presentation" />
            </div>
          </ScrollReveal>
          
          <ScrollReveal animation="scale-fade" delay={100}>
            <Card className="card-enhanced p-6 sm:p-8 shadow-xl border-0 group hover:shadow-2xl focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 transition-all duration-300" tabIndex={0} role="article" aria-labelledby="receive-content">
              <CardContent className="space-y-6 sm:space-y-8 text-center">
                <p id="receive-content" className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                  A clear snapshot of where your sales and marketing stand, and what to improve for future growth, delivered as a practical report.
                </p>
                
                <Button 
                  onClick={handleSubscribeClick}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 focus:bg-primary/90 text-primary-foreground text-lg sm:text-xl px-8 sm:px-12 py-4 sm:py-6 h-auto rounded-lg font-semibold shadow-lg hover:shadow-xl focus:shadow-xl focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-300 hover:scale-105 focus:scale-105 active:scale-95"
                  aria-label="Audit Now - Access the Marketing & Sales Self Audit tool">
                  Audit Now
                </Button>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--section-dark))]" role="region" aria-labelledby="company-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12 sm:mb-16">
              <h2 id="company-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">Growth Lens by SciMarq</h2>
              <div className="w-16 sm:w-20 h-1 bg-primary mx-auto" role="presentation" />
            </div>
          </ScrollReveal>
          
          <ScrollReveal animation="fade-left" delay={100}>
            <Card className="card-enhanced bg-card p-6 sm:p-8 border border-primary/20 group hover:border-primary/30 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 transition-all duration-300" tabIndex={0} role="article">
              <CardContent className="space-y-4 sm:space-y-6">
                <div className="space-y-3 sm:space-y-4 text-muted-foreground leading-relaxed text-sm sm:text-base">
                  <p>
                    Growth Lens is developed by <span className="text-[hsl(var(--green-dark))] font-semibold">SciMarq</span>, a B2B marketing consultancy that partners with SMEs to build structured, scalable growth models. We combine marketing science, behavioural economics and execution expertise to help businesses turn scattered activities into focused, result-driven strategies.
                  </p>
                  
                  <p>
                    We can also act as your extended marketing office for B2B growth. End-to-end support from strategy to execution, without the cost of an in-house team.
                  </p>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Enquiry Section */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--section-light))]" role="region" aria-labelledby="enquiry-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12 sm:mb-16">
              <h2 id="enquiry-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">Get in Touch</h2>
              <div className="w-16 sm:w-20 h-1 bg-primary mx-auto mb-4" role="presentation" />
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                Ready to accelerate your B2B growth? Contact our experts to discuss your marketing and sales challenges.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal animation="fade-up" delay={150}>
            <Suspense fallback={<div className="py-16 text-center text-muted-foreground">Loading form...</div>}>
              <div className="max-w-2xl mx-auto">
                <ContactForm
                  title="Send Us Your Enquiry"
                  submitButtonText="Submit Enquiry"
                  messagePlaceholder="Tell us about your business challenges and goals..."
                  successTitle="Enquiry Submitted Successfully"
                  successDescription="Thank you for your enquiry. We'll get back to you soon."
                />
              </div>
            </Suspense>
          </ScrollReveal>
        </div>
      </section>
      
      
      </div>
    </>
  );
};

export default Landing;
